/**
 * Pickeasy cart enhancements
 * - Show a Regional-specific delivery message when the Regional location is active
 *
 * Notes:
 * - Pickeasy can re-render its DOM, so we use observers and re-insert as needed
 * - Scoped to the cart page by how it's included (sections/main-cart.liquid)
 */
(() => {
  const REGIONAL_MESSAGE =
    "Regional delivery:\n\nOur courier will contact you directly to organise a time that suits you. Delivered to ground floor, usually within a week.\n\nLeave your preferred delivery weekdays as well as any additional notes below.";

  const WIDGET_SELECTOR = "pickeasy-app-widget";
  const REGIONAL_WIDGET_CLASS = "pickeasy--regional";
  const REGIONAL_MESSAGE_CLASS = "pickeasy-regional-message";
  const REGIONAL_ACTIVATION_DELAY_MS = 500;
  const REGIONAL_RECHECK_SUPPRESS_MS = 2500;
  const CHECK_LOCK_MIN_MS = 2000;
  const CHECK_LOCK_MAX_MS = 6000;

  // Pickeasy's translations contain full weekday names. Keep those available
  // to assistive technology, but use compact labels in the seven-column grid.
  const formatCalendarWeekdays = (widget) => {
    widget.querySelectorAll('.neo-date-grid tr.neo-equal-width > td.neo-day').forEach((cell) => {
      const label = cell.textContent.trim();
      if (label.length <= 3) return;
      cell.setAttribute('aria-label', label);
      cell.setAttribute('title', label);
      cell.textContent = Array.from(label).slice(0, 3).join('');
    });
  };

  const isVisible = (el) => {
    if (!el) return false;
    const style = window.getComputedStyle(el);
    if (style.display === "none") return false;
    if (style.visibility === "hidden") return false;
    if (Number(style.opacity) === 0) return false;
    if (el.offsetParent === null && style.position !== "fixed") return false;
    return true;
  };

  let pickeasyCartUpdateHookInstalled = false;
  const pickeasyCartUpdateListeners = new Set();

  const emitPickeasyCartUpdate = (attributes) => {
    for (const listener of pickeasyCartUpdateListeners) {
      try {
        listener(attributes);
      } catch {
        // ignore listener errors
      }
    }
  };

  const installPickeasyCartUpdateHook = () => {
    if (pickeasyCartUpdateHookInstalled) return;
    pickeasyCartUpdateHookInstalled = true;

    const proto = window.XMLHttpRequest?.prototype;
    if (!proto) return;

    const originalOpen = proto.open;
    const originalSend = proto.send;

    proto.open = function (...args) {
      // args: method, url, async?, user?, password?
      try {
        this.__pickeasyUrl = args[1];
      } catch {
        // ignore
      }
      return originalOpen.apply(this, args);
    };

    proto.send = function (...args) {
      try {
        this.addEventListener("load", () => {
          const url = String(this.__pickeasyUrl || this.responseURL || "");
          const isPickeasyCartUpdate =
            url.includes("/cart/update.js") && url.includes("app=pickeasy");

          if (!isPickeasyCartUpdate) return;
          if (this.status !== 200) return;

          const text = this.responseText;
          if (!text || text[0] !== "{") return;

          try {
            const data = JSON.parse(text);
            const attributes = data?.attributes;
            if (!attributes || typeof attributes !== "object") return;
            emitPickeasyCartUpdate(attributes);
          } catch {
            /* ignore malformed responses */
          }
        });
      } catch {
        // ignore
      }

      return originalSend.apply(this, args);
    };
  };

  const ensureRegionalMessageEl = (widget) => {
    const existing = widget.querySelector(`.${REGIONAL_MESSAGE_CLASS}`);
    if (existing) {
      if (existing.textContent !== REGIONAL_MESSAGE) existing.textContent = REGIONAL_MESSAGE;
      return existing;
    }

    const el = document.createElement("p");
    el.className = `additional-message ${REGIONAL_MESSAGE_CLASS}`;
    el.textContent = REGIONAL_MESSAGE;

    widget.appendChild(el);

    return el;
  };

  const positionRegionalMessageEl = (widget, el) => {
    // Prefer: directly after the delivery availability line
    const info = widget.querySelector("#delivery-location-info");
    if (info?.parentElement) {
      if (info.nextElementSibling !== el) info.insertAdjacentElement("afterend", el);
      return;
    }

    // Fallback: keep it near the delivery postcode area if present
    const deliveryLocations = widget.querySelector("#order-delivery-locations");
    if (deliveryLocations && el.parentElement !== deliveryLocations) {
      deliveryLocations.appendChild(el);
      return;
    }

    // Last resort: place after any existing message
    const anchor = widget.querySelector(".additional-message");
    if (anchor?.parentElement) {
      if (anchor.nextElementSibling !== el) anchor.insertAdjacentElement("afterend", el);
      return;
    }

    if (!widget.contains(el)) widget.appendChild(el);
  };

  const isRegionalDeliveryActive = (widget) => {
    // Only apply for delivery
    const activeOption = widget.querySelector(".ft-options.ft-options-active");
    const activeText = (activeOption?.textContent || "").toLowerCase();
    const isDelivery = activeText.includes("delivery");
    if (!isDelivery) return false;

    // Only after a postcode check has succeeded
    const info = widget.querySelector("#delivery-location-info");
    if (!info) return false;

    const style = (info.getAttribute("style") || "").toLowerCase();
    const isError = style.includes("color:red") || style.includes("color: red");
    if (isError) return false;

    const infoText = (info.textContent || "").toLowerCase();
    const isAvailable = infoText.includes("available");
    if (!isAvailable) return false;

    // Pickeasy marks the slot-picker heading as explicitly hidden for locations
    // where the date picker is disabled.
    const dateHeading = widget.querySelector("#orders-slots-heading.orders-slots-heading");
    const headingStyle = (dateHeading?.getAttribute("style") || "").toLowerCase();
    const headingExplicitlyHidden = headingStyle.includes("display: none");

    // Also verify the date input isn't visible (Pickeasy may keep it in the DOM)
    const dateInput = widget.querySelector("#neo-input-date");
    const dateIsVisible = dateInput ? isVisible(dateInput) : false;

    return headingExplicitlyHidden && !dateIsVisible;
  };

  const attachToWidget = (widget) => {
    if (widget.__pickeasyRegionalMessageAttached) return;
    widget.__pickeasyRegionalMessageAttached = true;

    installPickeasyCartUpdateHook();

    let raf = null;
    let regionalCandidateSince = null;
    let suppressRegionalUntil = 0;
    let lastKnownIsRegional = false;

    let isCheckLocked = false;
    let checkMinUnlockAt = 0;
    let sawPickeasyCartUpdateSinceLock = false;
    let checkUnlockTimer = null;
    let checkHardUnlockTimer = null;

    const normalizeZip = (value) => String(value || "").trim();
    let pendingZip = null;
    let lastConfirmedZip = null;

    const getCheckButton = () => widget.querySelector("#delivery-location-search");
    const getZipInput = () => widget.querySelector("#delivery-location-zip");

    const setCheckButtonLocked = (locked) => {
      const btn = getCheckButton();
      if (!btn) return;
      btn.disabled = locked;
      if (locked) btn.setAttribute("aria-disabled", "true");
      else btn.removeAttribute("aria-disabled");
    };

    const unlockCheckButton = () => {
      isCheckLocked = false;
      sawPickeasyCartUpdateSinceLock = false;
      checkMinUnlockAt = 0;

      if (checkUnlockTimer) window.clearTimeout(checkUnlockTimer);
      if (checkHardUnlockTimer) window.clearTimeout(checkHardUnlockTimer);
      checkUnlockTimer = null;
      checkHardUnlockTimer = null;

      setCheckButtonLocked(false);
      scheduleUpdate();
    };

    const maybeUnlockCheckButton = () => {
      if (!isCheckLocked) return;
      if (!sawPickeasyCartUpdateSinceLock) return;

      const now = performance.now();
      const remaining = checkMinUnlockAt - now;
      if (remaining > 0) {
        if (checkUnlockTimer) window.clearTimeout(checkUnlockTimer);
        checkUnlockTimer = window.setTimeout(unlockCheckButton, remaining);
        return;
      }

      unlockCheckButton();
    };

    const lockCheckButton = () => {
      if (isCheckLocked) return;

      isCheckLocked = true;
      sawPickeasyCartUpdateSinceLock = false;
      checkMinUnlockAt = performance.now() + CHECK_LOCK_MIN_MS;

      setCheckButtonLocked(true);

      if (checkHardUnlockTimer) window.clearTimeout(checkHardUnlockTimer);
      checkHardUnlockTimer = window.setTimeout(unlockCheckButton, CHECK_LOCK_MAX_MS);
    };

    const update = () => {
      raf = null;

      formatCalendarWeekdays(widget);

      const regionalMessage = ensureRegionalMessageEl(widget);
      // Position first to avoid visible "jump" when it becomes visible
      positionRegionalMessageEl(widget, regionalMessage);

      // If a postcode check is still in flight, avoid showing Regional messaging.
      // This reduces the chance of showing stale state while Pickeasy updates,
      // and also discourages rapid re-checks that can trigger Shopify 429s.
      if (isCheckLocked) {
        setCheckButtonLocked(true); // re-apply in case Pickeasy replaced the element
        widget.classList.remove(REGIONAL_WIDGET_CLASS);
        return;
      }

      // If the user has edited the postcode since the last completed check,
      // don't show Regional messaging based on stale UI from the previous postcode.
      const currentZip = normalizeZip(getZipInput()?.value);
      if (lastConfirmedZip && currentZip !== lastConfirmedZip) {
        regionalCandidateSince = null;
        widget.classList.remove(REGIONAL_WIDGET_CLASS);
        return;
      }

      const now = performance.now();

      // When switching from a Regional postcode to a Local one, Pickeasy can keep
      // the previous (Regional) DOM state around briefly. Suppress re-enabling
      // the Regional message during that transition to avoid flicker or "stuck"
      // regional messaging.
      if (now < suppressRegionalUntil) {
        regionalCandidateSince = null;
        widget.classList.remove(REGIONAL_WIDGET_CLASS);
        return;
      }

      const candidate = isRegionalDeliveryActive(widget);

      // Be conservative about enabling Regional: only after the UI has settled.
      // This avoids a brief flash if Pickeasy re-renders during a postcode change.
      let isRegional = false;
      if (candidate) {
        if (regionalCandidateSince == null) regionalCandidateSince = now;
        isRegional = now - regionalCandidateSince >= REGIONAL_ACTIVATION_DELAY_MS;
      } else {
        regionalCandidateSince = null;
      }

      widget.classList.toggle(REGIONAL_WIDGET_CLASS, isRegional);
      lastKnownIsRegional = isRegional;
    };

    const scheduleUpdate = () => {
      if (raf != null) return;
      raf = window.requestAnimationFrame(update);
    };

    pickeasyCartUpdateListeners.add(() => {
      if (!isCheckLocked) return;
      sawPickeasyCartUpdateSinceLock = true;
      if (pendingZip) lastConfirmedZip = pendingZip;
      maybeUnlockCheckButton();
      scheduleUpdate();
    });

    // Pickeasy can re-render parts of its UI after actions
    const mo = new MutationObserver(scheduleUpdate);
    mo.observe(widget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    const resetRegionalState = () => {
      if (lastKnownIsRegional) {
        suppressRegionalUntil = performance.now() + REGIONAL_RECHECK_SUPPRESS_MS;
      }
      regionalCandidateSince = null;
      widget.classList.remove(REGIONAL_WIDGET_CLASS);
    };

    widget.addEventListener(
      "click",
      (event) => {
        const target = event.target instanceof Element ? event.target : null;

        if (target?.closest("#delivery-location-search")) {
          pendingZip = normalizeZip(getZipInput()?.value) || null;

          // Lock the Check button after Pickeasy handles the click.
          // Without this, rapid Regional -> Local switching can trigger Shopify 429s,
          // which can leave Pickeasy stuck showing the Regional UI state.
          window.setTimeout(lockCheckButton, 0);

          // Only suppress if we're coming from Regional. This avoids delaying the
          // initial switch from Local to Regional.
          if (lastKnownIsRegional) {
            suppressRegionalUntil = performance.now() + REGIONAL_RECHECK_SUPPRESS_MS;
          }
          resetRegionalState();
        }

        scheduleUpdate();
      },
      true
    );

    widget.addEventListener(
      "input",
      (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.matches("#delivery-location-zip")) {
        resetRegionalState();
      }
      scheduleUpdate();
      },
      true
    );

    widget.addEventListener("change", scheduleUpdate, true);

    scheduleUpdate();
  };

  const init = () => {
    const existing = document.querySelector(WIDGET_SELECTOR);
    if (existing) attachToWidget(existing);

    const domObserver = new MutationObserver(() => {
      const widget = document.querySelector(WIDGET_SELECTOR);
      if (widget) attachToWidget(widget);
    });

    domObserver.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

